import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { matchFocusAreas, type FocusArea } from "@/lib/focusAreas";

export type CoachIntent = {
  capital: boolean;
  equity: boolean;
  advising: boolean;
  contracts: boolean;
};

/** Weight applied to each opportunity_type given the user's inferred intent. */
export function typeWeightForIntent(oppType: string | null | undefined, intent: CoachIntent): number {
  const t = (oppType ?? "grant").toLowerCase();
  let w = 0;
  if (intent.capital) {
    if (t === "loan" || t === "microloan" || t === "revolving_loan_fund") w += 12;
    if (t === "grant" || t === "tax_credit") w += 6;
  }
  if (intent.equity) {
    if (t === "equity_investment") w += 14;
    if (t === "loan") w += 4;
  }
  if (intent.advising) {
    if (t === "mentorship") w += 14;
    if (t === "technical_assistance") w += 12;
    if (t === "incubator") w += 8;
    if (t === "grant") w += 3;
  }
  if (intent.contracts) {
    if (t === "contract") w += 14;
    if (t === "technical_assistance") w += 6;
  }
  // Fallback: with no intent signal, mildly prefer grants (historic behavior).
  if (!intent.capital && !intent.equity && !intent.advising && !intent.contracts && t === "grant") {
    w += 3;
  }
  return w;
}

export function providerBonusForIntent(agency: string | null | undefined, intent: CoachIntent): number {
  const a = (agency ?? "").toUpperCase();
  if (!a) return 0;
  if (intent.advising && ["SCORE", "SBDC", "WBC", "MBDA", "VBOC"].includes(a)) return 5;
  if (intent.capital && ["TREASURY_CDFI", "USDA_RD", "SBA"].includes(a)) return 4;
  if (intent.contracts && ["MBDA", "SBA"].includes(a)) return 3;
  return 0;
}

function deriveIntent(fundingSought: string[], text: string): CoachIntent {
  const t = text.toLowerCase();
  const funding = new Set((fundingSought ?? []).map((s) => s.toLowerCase()));
  const capital =
    funding.has("loans") ||
    funding.has("grants") ||
    /\b(capital|loan|financing|working capital|cash flow|line of credit)\b/.test(t);
  const equity = funding.has("equity") || /\b(equity investment|venture|angel|investor)\b/.test(t);
  const contracts =
    funding.has("contracts/rfps") ||
    /\b(contract|contracts|rfp|procurement|8\(a\)|hubzone|wosb|sdvosb|government contract)\b/.test(t);
  const advising =
    /\b(coach|coaching|mentor|advising|technical assistance|capacity|how do i|help me|don'?t know|guidance|training)\b/.test(
      t,
    ) ||
    // If no funding option is selected, treat as advising-first.
    (fundingSought ?? []).length === 0;
  return { capital, equity, advising, contracts };
}

/**
 * Surfaces whether the signed-in user already has a readiness assessment on file
 * and, if so, infers their focus areas from their company profile so we can
 * highlight "Coach recommended" opportunities across the site.
 */
export function useCoachReadiness() {
  const { user } = useAuth();

  const { data, isLoading } = useQuery({
    enabled: !!user,
    queryKey: ["coach-readiness", user?.id],
    queryFn: async () => {
      const { data: profile } = await supabase
        .from("company_profiles")
        .select("id, industry, goals, funding_sought, county, blockers")
        .eq("user_id", user!.id)
        .maybeSingle();

      if (!profile) {
        return {
          hasProfile: false,
          hasAssessment: false,
          focusAreas: [] as FocusArea[],
          intent: { capital: false, equity: false, advising: false, contracts: false } as CoachIntent,
          profile: null,
        };
      }

      const { data: assessment } = await supabase
        .from("company_assessments")
        .select("id, created_at, summary, gaps, recommended_actions, funding_pathways")
        .eq("company_profile_id", profile.id)
        .order("created_at", { ascending: false })
        .limit(1)
        .maybeSingle();

      // Flatten any nested JSON values into a single searchable string so we
      // can pull focus-area signal out of the assessment as well as the profile.
      const flatten = (val: unknown): string => {
        if (!val) return "";
        if (typeof val === "string") return val;
        if (typeof val === "number" || typeof val === "boolean") return String(val);
        if (Array.isArray(val)) return val.map(flatten).join(" ");
        if (typeof val === "object") return Object.values(val as Record<string, unknown>).map(flatten).join(" ");
        return "";
      };

      const haystack = [
        profile.industry,
        profile.goals,
        (profile as { blockers?: string | null }).blockers,
        ...(profile.funding_sought ?? []),
        assessment?.summary,
        flatten(assessment?.gaps),
        flatten(assessment?.recommended_actions),
        flatten(assessment?.funding_pathways),
      ]
        .filter(Boolean)
        .join(" ");
      const focusAreas = matchFocusAreas(haystack);
      const intent = deriveIntent(profile.funding_sought ?? [], haystack);

      return {
        hasProfile: true,
        hasAssessment: !!assessment,
        focusAreas,
        intent,
        profile,
      };
    },
    staleTime: 5 * 60 * 1000,
  });

  return {
    loading: isLoading,
    hasProfile: data?.hasProfile ?? false,
    hasAssessment: data?.hasAssessment ?? false,
    focusAreas: (data?.focusAreas ?? []) as FocusArea[],
    intent: (data?.intent ?? { capital: false, equity: false, advising: false, contracts: false }) as CoachIntent,
    profile: data?.profile ?? null,
  };
}

/** Map GrantFinder's short area labels to canonical FocusArea strings. */
export const SHORT_AREA_TO_FOCUS: Record<string, FocusArea> = {
  Mobility: "Economic & Community Development",
  Climate: "Climate & Environment",
  Education: "Education & Youth",
  Justice: "Criminal Justice Reform",
  Health: "Health & Wellness",
};

export const FOCUS_TO_SHORT_AREA: Record<FocusArea, string> = {
  "Economic & Community Development": "Mobility",
  "Climate & Environment": "Climate",
  "Education & Youth": "Education",
  "Criminal Justice Reform": "Justice",
  "Health & Wellness": "Health",
};